import React, { useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { Tab, Tabs } from '@mui/material';

import OlcDataGrid from '~/components/General/OlcDataGrid';
import OlcTablePagination from '~/components/General/OlcTablePagination';
import { dateFormat } from '~/utils/date-format';

import { DASHBOARD_PAGE_SIZE, PERFORMANCE_MODES, PERFORMANCE_VIEWS } from '../../constants';
import useDashboardPagination from '../../hooks/useDashboardPagination';
import { formatCurrency, formatNumber, formatPercent, humanize } from '../../utils';

const getPerformanceRowPath = (row) => {
  if (!row?.entityIdValue) return null;

  if (row.entityType === 'orders') return `/orders/detail/overview/${row.entityIdValue}`;
  if (row.entityType === 'campaigns') return `/campaigns/edit/${row.entityIdValue}`;
  if (row.entityType === 'templates') return `/template-builder/${row.entityIdValue}`;

  return null;
};

const PerformanceTable = ({ dashboardWidgets, isLoading }) => {
  const navigate = useNavigate();
  const [performanceMode, setPerformanceMode] = useState(PERFORMANCE_MODES[0].value);
  const [performanceView, setPerformanceView] = useState('templates');
  const isHighestPerforming = performanceMode === 'highestPerforming';
  const activePerformanceView = isHighestPerforming ? 'templates' : performanceView;
  const entityLabel = humanize(activePerformanceView.slice(0, -1));
  const rankedPerformanceRows = useMemo(() => {
    const sourceRows = isHighestPerforming
      ? dashboardWidgets?.highestPerforming?.templates || []
      : dashboardWidgets?.mostMailed?.[activePerformanceView] || [];

    return sourceRows.map((row) => ({
      ...row,
      entityIdValue: row.id,
      entityType: activePerformanceView,
      id: `${performanceMode}-${activePerformanceView}-${row.id}`,
      uniqueResponders: Number(row.uniqueResponders ?? row.responses) || 0,
      entityId:
        activePerformanceView === 'orders'
          ? `Order #${row.id}`
          : `${activePerformanceView === 'campaigns' ? 'Campaign #' : 'ID: '}${row.id}`,
      mailed: row.mailedAt ? dateFormat(row.mailedAt, true, true) : 'Not yet mailed',
    }));
  }, [
    activePerformanceView,
    dashboardWidgets?.highestPerforming,
    dashboardWidgets?.mostMailed,
    isHighestPerforming,
    performanceMode,
  ]);
  const pagination = useDashboardPagination(rankedPerformanceRows, DASHBOARD_PAGE_SIZE);

  const performanceColumns = useMemo(() => {
    const sharedColumns = [
      {
        field: 'name',
        headerName: `${entityLabel} & ID`,
        minWidth: 220,
        flex: 1.5,
        sortable: false,
        compactValueGetter: (row) => `${row.name} ${row.entityId}`,
        renderCell: ({ row }) => (
          <div className="dashboard-primary-cell">
            <strong>{row.name}</strong>
            <small>{row.entityId}</small>
          </div>
        ),
      },
      {
        field: 'product',
        headerName: 'Product',
        minWidth: 150,
        flex: 1,
        sortable: false,
        compactValueGetter: (row) => row.product,
        renderCell: ({ row }) => row.product,
      },
      {
        field: 'mailed',
        headerName: activePerformanceView === 'orders' ? 'Mailed' : 'Last mailed',
        minWidth: 150,
        flex: 1,
        sortable: false,
        compactValueGetter: (row) => row.mailed,
        renderCell: ({ row }) => row.mailed,
      },
    ];

    if (isHighestPerforming) {
      return [
        ...sharedColumns,
        {
          field: 'delivered',
          headerName: 'Delivered',
          align: 'right',
          headerAlign: 'right',
          minWidth: 120,
          flex: 0.75,
          sortable: true,
          compactValueGetter: (row) => formatNumber(row.delivered),
          renderCell: ({ row }) => formatNumber(row.delivered),
        },
        {
          field: 'uniqueResponders',
          headerName: 'Unique responders',
          align: 'right',
          headerAlign: 'right',
          minWidth: 160,
          flex: 0.9,
          sortable: true,
          compactValueGetter: (row) => formatNumber(row.uniqueResponders),
          renderCell: ({ row }) => formatNumber(row.uniqueResponders),
        },
        {
          field: 'responseRate',
          headerName: 'Response rate',
          align: 'right',
          headerAlign: 'right',
          minWidth: 130,
          flex: 0.8,
          sortable: true,
          compactValueGetter: (row) => formatPercent(row.responseRate),
          renderCell: ({ row }) => formatPercent(row.responseRate),
        },
      ];
    }

    return [
      ...sharedColumns,
      {
        field: 'mailedPieces',
        headerName: 'Mailed',
        align: 'right',
        headerAlign: 'right',
        minWidth: 110,
        flex: 0.7,
        sortable: true,
        compactValueGetter: (row) => formatNumber(row.mailedPieces),
        renderCell: ({ row }) => formatNumber(row.mailedPieces),
      },
      {
        field: 'spend',
        headerName: 'Spend',
        align: 'right',
        headerAlign: 'right',
        minWidth: 110,
        flex: 0.7,
        sortable: false,
        compactValueGetter: (row) => formatCurrency(row.spend),
        renderCell: ({ row }) => formatCurrency(row.spend),
      },
    ];
  }, [activePerformanceView, entityLabel, isHighestPerforming]);

  const emptyStateText = isHighestPerforming
    ? 'No tracked template performance yet.'
    : `No ${performanceView} have been mailed yet.`;
  const performanceDescription = isHighestPerforming
    ? 'The templates getting the strongest response from recipients.'
    : 'Your highest-volume sends - the orders, templates, and campaigns you mail the most.';
  const handleRowClick = ({ row }) => {
    const path = getPerformanceRowPath(row);
    if (path) navigate(path);
  };

  return (
    <section className="dashboard-table-section performance-section">
      <div className="section-heading performance-heading">
        <div>
          <div className="heading-row">
            <Tabs
              value={performanceMode}
              onChange={(_, nextMode) => {
                setPerformanceMode(nextMode);
                if (nextMode === 'highestPerforming') {
                  setPerformanceView('templates');
                }
                pagination.resetPage();
              }}
              className="dashboard-text-tabs dashboard-performance-mode-tabs"
              aria-label="Performance metric"
              TabIndicatorProps={{ className: 'dashboard-tab-indicator' }}
            >
              {PERFORMANCE_MODES.map((mode) => (
                <Tab
                  key={mode.value}
                  value={mode.value}
                  label={mode.label}
                  className="dashboard-text-tab"
                  disableRipple
                />
              ))}
            </Tabs>
            <span className="time-pill">All time</span>
          </div>
          <p>{performanceDescription}</p>
        </div>
        {!isHighestPerforming && (
          <Tabs
            value={performanceView}
            onChange={(_, nextView) => {
              setPerformanceView(nextView);
              pagination.resetPage();
            }}
            className="dashboard-text-tabs dashboard-performance-tabs"
            aria-label="Performance type"
            TabIndicatorProps={{ className: 'dashboard-tab-indicator' }}
          >
            {PERFORMANCE_VIEWS.map((view) => (
              <Tab
                key={view}
                value={view}
                label={humanize(view)}
                className="dashboard-text-tab"
                disableRipple
              />
            ))}
          </Tabs>
        )}
      </div>

      <div className="dashboard-grid-shell">
        <OlcDataGrid
          key={`${performanceMode}-${activePerformanceView}`}
          rows={pagination.visibleRows}
          columns={performanceColumns}
          loading={isLoading}
          hideFooter
          rowSelection={false}
          disableRowSelectionOnClick
          disableColumnMenu
          rowHeight={61}
          columnHeaderHeight={48}
          getRowId={(row) => row.id}
          initialState={{
            sorting: {
              sortModel: [
                { field: isHighestPerforming ? 'responseRate' : 'mailedPieces', sort: 'desc' },
              ],
            },
          }}
          onRowClick={handleRowClick}
          slots={{
            noRowsOverlay: () => <div className="dashboard-grid-empty">{emptyStateText}</div>,
          }}
        />
        {(isLoading || rankedPerformanceRows.length > 0) && (
          <OlcTablePagination
            count={pagination.pageCount}
            page={pagination.page}
            pageSize={pagination.pageSize}
            onPageChange={(_, nextPage) => pagination.setPage(nextPage)}
            from={pagination.from}
            to={pagination.to}
            total={pagination.total}
            loading={isLoading}
            showPageSize={false}
          />
        )}
      </div>
    </section>
  );
};

export default PerformanceTable;
